import { extendTheme, StyleFunctionProps } from '@chakra-ui/react';
import { mode } from '@chakra-ui/theme-tools';

import { Alert, Input, Link, Menu, Slider, Table } from './components';
/*
  The following has been adapted from these Reference docs:

  https://chakra-ui.com/docs/features/color-mode
  https://chakra-ui.com/docs/theming/theme
  https://github.com/chakra-ui/chakra-ui/issues/591#issuecomment-719751857

  The layout for the theme customization uses recommendations from Chakra on
  a more scalable structure:

  https://chakra-ui.com/docs/theming/customize-theme#scaling-out-your-project
*/
import { styles } from './styles';

// Shared intensity colors for heatmaps/campaign visuals
export const intensityColors = [
  '#EAF7F0', // Very light green
  '#D2F0E0', // Light green
  '#A1E1BE', // Light-medium green
  '#66C98D', // Medium green
  '#27AE60', // Dark green
] as const;

export const getIntensityColor = (value: number | string, type?: string) => {
  if (typeof value === 'string') {
    if (type === 'holiday') return '#8B5CF6';
    if (type === 'competitor') {
      switch (value) {
        case 'high':
          return '#B0B0B0';
        case 'medium':
          return '#D0D0D0';
        case 'low':
          return '#E8E8E8';
        default:
          return '#D0D0D0';
      }
    }
    switch (value) {
      case 'low':
        return intensityColors[0];
      case 'medium':
        return intensityColors[3];
      case 'high':
        return intensityColors[4];
      default:
        return intensityColors[2];
    }
  }
  if (value <= 2) return intensityColors[0];
  if (value <= 3.5) return intensityColors[1];
  if (value <= 5) return intensityColors[2];
  if (value <= 7) return intensityColors[3];
  return intensityColors[4];
};

export const baseColorsForCharts = [
  '#27AE60',
  '#FF8A65',
  '#A1887F',
  '#F06292',
  '#FFCC80',
  '#CE93D8',
  '#00897B',
  '#4DD0E1',
  '#AB47BC',
  '#1E88E5',
  '#66BB6A',
  '#3949AB',
  '#9FA8DA',
  '#90CAF9',
  '#800080',
  '#FF80FF',
  '#80FFFF',
  '#FFC0CB',
  '#B0E0E6',
  '#DDA0DD',
  '#8B008B',
  '#556B2F',
  '#008000',
  '#800000',
  '#000080',
  '#FFA500',
  '#A0522D',
  '#008080',
  '#FFD700',
  '#FF69B4',
  '#4B0082',
  '#FFFFF0',
  '#F0E68C',
  '#E6E6FA',
  '#FF7F50',
  '#00FF7F',
  '#FFB6C1',
  '#9400D3',
  '#8B0000',
  '#FF6347',
  '#DC143C',
  '#000000',
  '#696969',
  '#808080',
  '#A9A9A9',
  '#C0C0C0',
  '#D3D3D3',
  '#F5F5F5',
  '#B22222',
  '#FA8072',
  '#FFA07A',
  '#BC8F8F',
  '#BEBEBE',
  '#7B68EE',
  '#9370DB',
  '#9400D3',
  '#FF1493',
  '#CD5C5C',
  '#FF4500',
  '#FAEBD7',
  '#8B4513',
  '#2F4F4F',
  '#7FFF00',
  '#00CED1',
  '#20B2AA',
  '#191970',
  '#4B0082',
  '#FF8C00',
  '#696969',
  '#008B8B',
  '#00FA9A',
  '#808000',
  '#008080',
  '#483D8B',
  '#6495ED',
  '#00BFFF',
  '#1E90FF',
  '#87CEEB',
  '#ADD8E6',
  '#4169E1',
  '#0000CD',
  '#00008B',
  '#B0C4DE',
  '#F0F8FF',
  '#AFEEEE',
  '#E0FFFF',
  '#FAF0E6',
  '#FFF5EE',
  '#F5DEB3',
  '#FFFACD',
  '#F0FFF0',
  '#FFFFE0',
  '#FAFAD2',
  '#FFE4B5',
  '#FFE4C4',
  '#FFDAB9',
  '#FFEBCD',
  '#FFEFD5',
  '#FFF8DC',
  '#F5F5DC',
  '#FFFFF0',
];

const lightColors = baseColorsForCharts.map((c, i) => {
  if (i < 2) {
    return c;
  }
  return `${c}BB`;
});

const darkColors = baseColorsForCharts.map((c, i) => {
  if (i < 2) {
    return c;
  }
  return `${c}66`;
});

export const benchmarkColor = '#27AE60';

const theme = extendTheme({
  config: {
    // First visit follows the OS preference; the tri-state user override
    // (light/dark/system) is managed by ThemePreferenceContext.
    initialColorMode: 'system',
    useSystemColorMode: false,
  },
  fontSizes: {
    '4.5xl': '2.5rem',
  },
  fonts: {
    heading: 'Nunito Sans, sans-serif',
    body: 'Nunito Sans, sans-serif',
    marketingPage: 'Instrument Sans Variable, sans-serif',
  },
  colors: {
    background: {
      light: {
        highest: '#FFFFFF',
        lowest: '#F2F2F2',
        hover: '#F2F2F2',
        error: '#A13D61',
      },
      dark: {
        highest: '#1E293A',
        lowest: '#0c121d',
        hover: '#26334A',
        topbar: '#101927',
        error: '#A13D61',
      },
      paleGrey: '#F0F2F6',
      lightGrey: '#F8F8FA',
      lightBlue: '#A4B1C8',
      lightGreen: '#27AE600A',
      red: {
        500: '#EC221F',
      },
      green: {
        200: '#CFF7D3',
      },
    },
    border: {
      light: {
        box: '#DCE1EA',
        error: '#A13D61',
        focus: '#644CC3',
        gray: {
          50: '#F3F3F3',
        },
      },
      dark: {
        box: '#334155',
        error: '#CC6478',
        focus: '#644CC3',
        gray: {
          50: '#F3F3F3',
        },
      },
      red: '#FF375E',
      lightGreen: '#27AE6033',
    },
    text: {
      light: {
        primary: '#414B64',
        secondary: '#6B758F',
        highContrast: '#14171F',
        benchmark: benchmarkColor,
        active: '#3D8EB1',
        error: '#A13D61',
        errorBox: '#FFFFFF',
        pageTitle: '#000000',
      },
      dark: {
        primary: '#C0C1C7',
        secondary: '#838593',
        highContrast: '#ECEDEE',
        benchmark: benchmarkColor,
        active: '#5EB1D7',
        error: '#CC6478',
        errorBox: '#ECEDEE',
        pageTitle: '#FFFFFF',
      },
      darkBlue: {
        900: '#1E1E2F',
        800: '#2C3E50',
        950: '#1D252D',
      },
      lightGrey: '#F8F8FA',
      darkGray: '#92A5B8',
      lightGreen: '#5DBC85',
      red: '#F04438',
    },
    icon: {
      light: {
        color: '#A0ADC5',
        activeColor: '#14171F',
        background: '#FFFFFF',
        activeBackground: '#F0F2F6',
        hoverBackground: '#F5F7F9',
      },
      dark: {
        color: '#64748B',
        activeColor: '#ECEDEE',
        background: '#1E293A',
        activeBackground: '#334155',
        hoverBackground: '#26334A',
      },
      green: '#27AE60',
      red: '#D43E38',
      blue: '#1E88E5',
    },
    button: {
      light: {
        500: '#414B64',
        600: '#6B758F',
        700: '#CCD3E0',
      },
      dark: {
        200: '#C7C9CE',
        300: '#ECEDEE',
        400: '#838593',
      },
    },
    // Colors that can be used in charts, from bottom to top.
    chart: {
      grid: {
        light: '#E5E9F0',
        dark: '#334155',
      },
      dataColors: {
        light: lightColors,
        dark: darkColors,
      },
      benchmarkLine: benchmarkColor,
      // For scatter/line charts /Pie charts
      line: {
        light: lightColors,
        dark: darkColors,
        blue: '#3949AB',
      },
      // Bar
      bar: {
        light: lightColors,
        dark: darkColors,
        lightGreen: '#8BEAB2',
      },
    },
    status: {
      background: {
        success: '#ECFDF3',
        running: '#d4e9ff',
        fail: '#ffd4d2',
        not_started: '#C7C9CE',
      },
      text: {
        success: '#27AE60',
        running: '#1E88E5',
        fail: '#D43E38',
        not_started: '#FFFFFF',
      },
    },
    omnicomBlue: '#2C3865',
  },
  semanticTokens: {
    colors: {
      'surface.card': {
        default: 'background.light.highest',
        _dark: 'background.dark.highest',
      },
      'surface.sidebar': {
        default: 'white',
        _dark: 'background.dark.highest',
      },
      'surface.topbar': {
        default: 'white',
        _dark: 'background.dark.topbar',
      },
      'bg.layout': {
        default: 'background.light.lowest',
        _dark: 'background.dark.lowest',
      },
      'surface.hover': {
        default: 'background.light.hover',
        _dark: 'background.dark.hover',
      },
      'surface.control': {
        default: 'text.lightGrey',
        _dark: 'background.dark.hover',
      },
      'surface.overlay': {
        default: 'white',
        _dark: 'background.dark.lowest',
      },
      'surface.tooltip': {
        default: 'white',
        _dark: '#334154',
      },
      'surface.greenHover': {
        default: 'green.50',
        _dark: 'rgba(39, 174, 96, 0.16)',
      },
      'surface.controlHover': {
        default: 'gray.200',
        _dark: 'whiteAlpha.300',
      },
      'status.bg.success': {
        default: 'status.background.success',
        _dark: 'rgba(39, 174, 96, 0.16)',
      },
      'status.fg.success': {
        default: 'status.text.success',
        _dark: '#5DBC85',
      },
      'status.bg.running': {
        default: 'status.background.running',
        _dark: 'rgba(30, 136, 229, 0.16)',
      },
      'status.fg.running': {
        default: 'status.text.running',
        _dark: '#64B5F6',
      },
      'status.bg.fail': {
        default: 'status.background.fail',
        _dark: 'rgba(212, 62, 56, 0.16)',
      },
      'status.fg.fail': {
        default: 'status.text.fail',
        _dark: '#F08380',
      },
      'status.bg.not_started': {
        default: 'status.background.not_started',
        _dark: 'whiteAlpha.300',
      },
      'status.fg.not_started': {
        default: 'status.text.not_started',
        _dark: '#ECEDEE',
      },
      'text.title': {
        default: 'text.darkBlue.900',
        _dark: 'text.dark.highContrast',
      },
      'text.body': {
        default: 'text.light.primary',
        _dark: 'text.dark.primary',
      },
      'text.muted': {
        default: 'text.light.secondary',
        _dark: 'text.dark.secondary',
      },
      'border.layout': {
        default: 'gray.200',
        _dark: 'whiteAlpha.200',
      },
      'border.box': {
        default: 'border.light.box',
        _dark: 'border.dark.box',
      },
      'border.subtle': {
        default: 'border.light.gray.50',
        _dark: 'whiteAlpha.200',
      },
    },
  },
  styles,
  components: {
    Alert,
    Button: {
      baseStyle: {
        borderRadius: '4px',
      },
    },
    Toast: {
      baseStyle: (props: StyleFunctionProps) => ({
        container: {
          bg: mode('white', 'background.dark.highest')(props),
          color: mode('text.light.primary', 'text.dark.primary')(props),
          border: '1px solid',
          borderColor: mode('#DCE1EA', 'whiteAlpha.300')(props),
          borderRadius: '6px',
          boxShadow: 'lg',
        },
      }),
      variants: {
        success: (props: StyleFunctionProps) => ({
          container: {
            bg: mode('#F0FFF4', 'rgba(39, 174, 96, 0.16)')(props),
            color: mode('#22543D', '#9AE6B4')(props),
            borderColor: mode('#9AE6B4', 'rgba(39, 174, 96, 0.45)')(props),
          },
        }),
        'success-white': (props: StyleFunctionProps) => ({
          container: {
            bg: mode('white !important', '#1E293A !important')(props),
            border: '1px solid #27AE60 !important',
            outline: 'none !important',
            boxShadow: 'none !important',
            borderRadius: '12px !important',
            padding: '16px !important',
            fontSize: '14px !important',
            fontWeight: '500 !important',
            maxWidth: '500px !important',
            minWidth: '320px !important',
            width: 'auto !important',
          },
        }),
        error: (props: StyleFunctionProps) => ({
          container: {
            bg: mode('#FED7D7', 'rgba(212, 62, 56, 0.16)')(props),
            color: mode('#742A2A', '#FEB2B2')(props),
            borderColor: mode('#FC8181', 'rgba(212, 62, 56, 0.45)')(props),
          },
        }),
        warning: (props: StyleFunctionProps) => ({
          container: {
            bg: mode('#FFFBEB', 'rgba(246, 224, 94, 0.14)')(props),
            color: mode('#744210', '#F6E05E')(props),
            borderColor: mode('#F6E05E', 'rgba(246, 224, 94, 0.45)')(props),
          },
        }),
        info: (props: StyleFunctionProps) => ({
          container: {
            bg: mode('#EBF8FF', 'rgba(30, 136, 229, 0.16)')(props),
            color: mode('#2A4365', '#90CDF4')(props),
            borderColor: mode('#90CDF4', 'rgba(30, 136, 229, 0.45)')(props),
          },
        }),
      },
    },
    Input,
    Link,
    Menu,
    Table,
    Slider,
  },
});

export default theme;
